import { createSelector } from 'reselect'

import { selectCurrentContactId } from 'ducks/crm/contacts'
import { selectCurrentTicketId, selectTicketsById } from 'selectors/tickets'
import { selectAgentsById } from 'selectors/agents/base'
import { selectAccountPreferenceSortByCollaboratorCommentEnabled } from 'selectors/app/selectAccountPreferences'
import { emptyObj } from 'util/objects'
import { selectBase as selectRequests } from 'ducks/requests'
import { selectRoomsById } from 'ducks/chat/selectors/rooms'

import { CONVERSATION, ROOM } from './types'

function selectBase(state) {
  return state?.integrations?.recentConversations || emptyObj
}

export const selectRecentConversationsAndChatsForCurrentContact = createSelector(
  selectBase,
  selectCurrentContactId,
  selectCurrentTicketId,
  selectTicketsById,
  selectAgentsById,
  selectRoomsById,
  selectAccountPreferenceSortByCollaboratorCommentEnabled,
  (
    base,
    contactId,
    currentTicketId,
    ticketsById,
    agentsById,
    roomsById,
    sortByCollaboratorCommentAtEnabled
  ) => {
    const recentConversations = base[contactId]
    return recentConversations?.map(
      ({
        assignee: recentAssignee,
        id,
        isRead,
        name,
        number,
        state: recentState,
        subject: recentSubject,
        type,
        updatedAt: recentUpdatedAt,
      }) => {
        if (type === CONVERSATION) {
          // We have to extract and merge in data from the redux
          // store to ensure optimistic updates are reflected
          const reduxTicket = ticketsById[number]
          const agent = agentsById[reduxTicket?.assignee?.id]
          const reduxState = reduxTicket?.snoozedUntil
            ? 'snoozed'
            : reduxTicket?.state
          const state = reduxTicket ? reduxState : recentState?.toLowerCase()

          // TODO: (convo-reordering-after-full-migrations): remove updated_at
          const {
            latest_collaborator_comment_at: latestCollaboratorCommentAt,
            updated_at: ticketUpdatedAt,
          } =
            reduxTicket || {}

          const updatedAt =
            (sortByCollaboratorCommentAtEnabled
              ? latestCollaboratorCommentAt
              : ticketUpdatedAt) || recentUpdatedAt

          return {
            assignee: agent ? agent?.username : recentAssignee,
            isCurrent: number.toString() === currentTicketId.toString(),
            id,
            number,
            state,
            title: name || reduxTicket ? reduxTicket?.title : recentSubject,
            type,
            updatedAt,
            from: 'email',
          }
        } else if (type === ROOM) {
          let from = 'email'

          const reduxRoom = roomsById[id]
          const agent =
            agentsById[
              reduxRoom?.agentId?.replace('ag_', '') ||
                recentAssignee?.id?.replace('ag_', '')
            ]
          const state = reduxRoom
            ? reduxRoom?.state
            : recentState?.toLowerCase()

          if (reduxRoom?.facebookEvent) {
            from = 'messenger'
          } else {
            from = 'chat'
          }

          return {
            assignee: agent?.username,
            isCurrent: number.toString() === currentTicketId.toString(),
            id,
            number,
            state: isRead === false ? 'unread' : state,
            title: name,
            type,
            updatedAt: reduxRoom?.updatedAt || recentUpdatedAt,
            from,
          }
        }
        return null
      }
    )
  }
)

export const selectIsOrWillBeLoadingRecentConversationsAndChatsForCurrentContact = createSelector(
  selectCurrentContactId,
  selectRequests,
  (contactId, requests) => {
    return (
      requests && requests[`fetchRecentConversations-${contactId}`]?.loading
    )
  }
)
